/**
 * Status Update Modal Component
 * 
 * This component provides a modal interface for updating order status
 * with validation and available transitions.
 */

import React, { useState, useEffect } from 'react';
import { useTranslations } from '../../context/TranslationsContext';
import { useApp } from '../../context/AppContext';
import { ordersAPI } from '../../api';

const StatusUpdateModal = ({ order, onClose, onStatusUpdated }) => {
  const { translations } = useTranslations();
  const { state } = useApp();
  const t = translations[state.currentLanguage];

  const [selectedStatus, setSelectedStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [validTransitions, setValidTransitions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (order) {
      setAdminNotes(order.admin_notes || '');
      fetchValidTransitions();
    }
  }, [order]);

  const fetchValidTransitions = async () => {
    try {
      setLoading(true);
      const transitions = await ordersAPI.getValidTransitions(state.token, order.id);
      setValidTransitions(transitions.available_transitions || []);
      setError('');
    } catch (err) {
      console.error('Error fetching valid transitions:', err);
      setError('Failed to load available status transitions');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!selectedStatus) {
      setError('Please select a status');
      return;
    }

    try {
      setLoading(true);
      setError('');

      await ordersAPI.updateOrderStatus(state.token, order.id, {
        status: selectedStatus,
        admin_notes: adminNotes
      });

      // Call parent callback to refresh data
      onStatusUpdated();
      onClose();
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update order status');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoTransition = async () => {
    try {
      setLoading(true);
      setError('');

      await ordersAPI.transitionOrderStatus(state.token, order.id, {
        admin_notes: adminNotes || 'Auto transition'
      });

      // Call parent callback to refresh data
      onStatusUpdated();
      onClose();
    } catch (err) {
      console.error('Error auto transitioning:', err);
      setError('Failed to auto transition order status');
    } finally {
      setLoading(false);
    }
  };

  if (!order) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>{t.updateStatus}</h3>
          <button onClick={onClose} className="close-btn">×</button>
        </div>
        
        <div className="modal-body">
          <div className="order-info">
            <h4>Order #{order.id.substring(0, 8)}</h4>
            <p><strong>{t.status}:</strong> {t[order.status] || order.status}</p>
            <p><strong>{t.customerName}:</strong> {order.customer_name}</p>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label>{t.selectStatus}:</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="form-input"
              disabled={loading}
            >
              <option value="">{t.selectStatus}</option>
              {validTransitions.map(status => (
                <option key={status} value={status}>
                  {t[status] || status}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>{t.notes}:</label>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              className="form-input"
              rows="3"
              placeholder="Add notes about this status change..."
              disabled={loading}
            />
          </div>

          <div className="valid-transitions">
            <h4>{t.validTransitions}:</h4>
            <div className="transitions-list">
              {validTransitions.length > 0 ? (
                validTransitions.map(status => (
                  <span key={status} className="transition-tag">
                    {t[status] || status}
                  </span>
                ))
              ) : (
                <div className="no-transitions">
                  {order.status === 'completed' 
                    ? (state.currentLanguage === 'ar' 
                        ? 'هذا الطلب مكتمل ولا يمكن تغيير حالته'
                        : 'This order is completed and cannot be changed')
                    : order.status === 'rejected'
                    ? (state.currentLanguage === 'ar'
                        ? 'هذا الطلب مرفوض ولا يمكن تغيير حالته'
                        : 'This order is rejected and cannot be changed')
                    : (state.currentLanguage === 'ar'
                        ? 'لا توجد انتقالات متاحة لهذا الطلب حالياً'
                        : 'No valid transitions available for this order')
                  }
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <div className="footer-left">
            {validTransitions.length > 0 ? (
              <>
                <button
                  onClick={handleStatusUpdate}
                  className="btn-primary"
                  disabled={loading || !selectedStatus}
                >
                  {loading ? (state.currentLanguage === 'ar' ? 'جاري التحديث...' : 'Updating...') : t.updateStatus}
                </button>
                
                <button
                  onClick={handleAutoTransition}
                  className="btn-secondary"
                  disabled={loading}
                >
                  {loading ? (state.currentLanguage === 'ar' ? 'جاري الانتقال...' : 'Transitioning...') : t.autoTransition}
                </button>
              </>
            ) : (
              <div className="no-actions-message">
                {state.currentLanguage === 'ar' 
                  ? 'غير قابل للتغيير'
                  : 'Cannot be changed'
                }
              </div>
            )}
          </div>
          
          <div className="footer-right">
            <button
              onClick={onClose}
              className="btn-secondary"
              disabled={loading}
            >
              {t.cancel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusUpdateModal;
